import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { useCertificationRecordData } from './useCertificationRecordData';
import { useCallback } from 'react';
import { CertificationRecord } from '../types/types';
import {
  CostCodeWithCertificationsQuery,
  CostCodeWithCertificationsQueryVariables,
  Member,
} from '__generated__/graphql';
import { COST_CODE_WITH_CERTIFICATIONS_QUERY } from 'apollo/queries/cost-code-queries';
import { isNil } from 'lodash';
import { MemberMissingCerts } from './useCertificationChecker';
import { getFullNameFromMember } from 'utils/stringUtils';
import { NonNullArrayField } from 'types/util/NonNullArrayField';

export type CostCodeCertificationLink = NonNullArrayField<
  NonNullArrayField<CostCodeWithCertificationsQuery['costCodes']>['certificationLinks']
>;

export function useCostCodeCertificationChecker() {
  const client = useGraphQLClient();
  const getCertificationRecords = useCertificationRecordData();

  const hasMissingCostCodeCertifications = useCallback(
    async (memberIds: string[], costCodeIds: string[]) => {
      if (costCodeIds.length === 0) {
        return false;
      }

      const certificationRecords: CertificationRecord[] = await getCertificationRecords(memberIds);

      const costCodeResult = await client.request<
        CostCodeWithCertificationsQuery,
        CostCodeWithCertificationsQueryVariables
      >({
        document: COST_CODE_WITH_CERTIFICATIONS_QUERY,
        variables: {
          filter: {
            id: { contains: costCodeIds },
          },
        },
      });

      if (isNil(costCodeResult.costCodes) || costCodeResult.costCodes.length === 0) {
        throw new Error('Cost code not found');
      }

      const requiredCertIds: string[] = [];

      costCodeResult.costCodes.forEach((costCode) => {
        costCode.certificationLinks?.forEach((cert) => {
          if (cert?.certificationId && cert.certification?.name && isNil(cert.deletedOn)) {
            requiredCertIds.push(cert.certificationId);
          }
        });
      });

      const uniqueRequiredCertIds = Array.from(new Set(requiredCertIds));

      const recordsByMember: Map<string, string[]> = new Map();

      memberIds.forEach((memberId) => {
        recordsByMember.set(
          memberId,
          certificationRecords.filter((record) => record.memberId === memberId).map((record) => record.certificationId)
        );
      });

      return !Array.from(recordsByMember.values()).every((certIds) =>
        uniqueRequiredCertIds.every((reqId) => certIds.includes(reqId))
      );
    },
    [client, getCertificationRecords]
  );

  const getMissingCostCodeCertifications = useCallback(
    async (members: Member[], costCodeIds: string[]): Promise<MemberMissingCerts[]> => {
      if (costCodeIds.length === 0) {
        return [];
      }

      const memberIds = members.map((m) => m.id);
      const certificationRecords: CertificationRecord[] = await getCertificationRecords(memberIds);

      const costCodeResult = await client.request<
        CostCodeWithCertificationsQuery,
        CostCodeWithCertificationsQueryVariables
      >({
        document: COST_CODE_WITH_CERTIFICATIONS_QUERY,
        variables: {
          filter: {
            id: { contains: costCodeIds },
          },
        },
      });

      if (isNil(costCodeResult.costCodes) || costCodeResult.costCodes.length === 0) {
        throw new Error('Cost code not found');
      }

      const requiredCertMap = new Map<string, string>();

      costCodeResult.costCodes.forEach((costCode) => {
        costCode.certificationLinks?.forEach((cert) => {
          if (
            cert?.certificationId &&
            cert.certification?.name &&
            isNil(cert.deletedOn) &&
            !requiredCertMap.has(cert.certificationId)
          ) {
            requiredCertMap.set(cert.certificationId, cert.certification.name);
          }
        });
      });

      const requiredCertIds = Array.from(requiredCertMap.keys());

      const recordsByMember: MemberMissingCerts[] = members.map((member) => {
        const memberCertIds = certificationRecords
          .filter((record) => record.memberId === member.id)
          .map((record) => record.certificationId);

        const missingCerts = requiredCertIds.filter((certId) => !memberCertIds.includes(certId));

        return {
          memberId: member.id,
          memberName: getFullNameFromMember(member),
          missingCerts: missingCerts.map((id) => requiredCertMap.get(id)!),
        };
      });

      return recordsByMember;
    },
    [client, getCertificationRecords]
  );

  return { hasMissingCostCodeCertifications, getMissingCostCodeCertifications };
}
